import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ArrowUpRight } from "lucide-react";
import { PageHero, PageCta } from "@/components/PageParts";
import { usePageMeta } from "@/hooks/usePageMeta";
import { services } from "@/data/services";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  usePageMeta(
    service ? `${service.title} — May Forward Tech` : "May Forward Tech",
    service ? service.intro.slice(0, 150) : ""
  );
  if (!service) return <Navigate to="/services" replace />;

  const others = services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <PageHero eyebrow={service.badge} title={service.title} intro={service.intro} image={service.image} />

      <section className="py-16 lg:py-24 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#0FA89E] font-semibold mb-4">
              What you get
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
              Outcomes, not deliverables
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
            {service.outcomes.map((o, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                className="flex items-start gap-4 rounded-xl border border-slate-200 bg-[#FAFAFC] p-5"
              >
                <span className="shrink-0 w-7 h-7 rounded-full bg-[rgba(15,168,158,0.1)] border border-[rgba(15,168,158,0.3)] flex items-center justify-center mt-0.5">
                  <Check className="w-4 h-4 text-[#0FA89E]" />
                </span>
                <p className="text-base text-slate-700 leading-relaxed">{o}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-[#FAFAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-2xl mb-12">
            <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#0FA89E] font-semibold mb-4">
              Engagement models
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
              Three ways to work with us
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {service.engagements.map((e, i) => (
              <motion.div
                key={i}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="group relative rounded-2xl border border-slate-200 bg-white p-7 lg:p-8 transition-all duration-300 hover:border-[rgba(15,168,158,0.4)] hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(15,23,42,0.15)]"
              >
                <span className="font-mono-tech text-sm font-semibold text-[#F5A400]">
                  0{i + 1}
                </span>
                <h3 className="font-display text-xl font-semibold tracking-tight text-[#0F172A] mt-4 mb-3">
                  {e.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{e.detail}</p>
                <div className="absolute bottom-0 left-7 right-7 h-[2px] bg-[#0FA89E] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 rounded-full" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-[#F3F4F8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-slate-400 mb-6">
            Also explore
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {others.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 transition-colors hover:border-[rgba(15,168,158,0.4)] hover:text-[#0FA89E]"
              >
                {s.nav}
                <ArrowUpRight className="w-4 h-4 shrink-0 text-[#0FA89E] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <PageCta />
    </>
  );
}
